import {
    AddDnsSearchDomainRequest,
    AddExtraDnsRecordRequest,
    AddExtraDnsRequest,
    Configuration,
    CreateAccessRuleRequest,
    CreateLocalGroupRequest,
    CreatePreAuthKeyRequest,
    CreateSplitDnsDomainRequest,
    CreateSplitDnsResolverRequest,
    CreateTagRequest,
    DefaultApi,
    EnableSubnetRequest,
    FullGlobalDns,
    GetAllDevicesRequest,
    GetAllExitNodesRequest,
    GetAllExtraDnsRequest,
    GetAllGroupsRequest,
    GetAllPreAuthKeysRequest,
    GetAllSubnetsRequest,
    GetAllTagsRequest,
    GetAllUsersRequest,
    GetChangelogRequest,
    GetPendingExitNodesRequest,
    GetPendingRoutesRequest,
    GetSingleGroupMembersRequest,
    MoveDnsSearchDomainRequest,
    RequiredError,
    ResponseError,
    SetDeviceDnsnameRequest,
    SetGeneralSettingsRequest,
    SetLdapConnectionSettingsRequest,
    SetLdapQuerySettingsRequest,
    SetScimSettingsRequest,
    SubnetsDescriptionRequest,
    UpdateAccessRuleRequest,
    UpdateExtraDnsDomainRequest,
    UpdateExtraDnsRecordRequest,
    UpdateGroupRequest,
    UpdateOidcSettingsRequest,
    UpdateSplitDnsDomainRequest,
    UpdateSplitDnsResolverRequest,
} from "src/api/generated/frontend_internal_v1";
import { ERROR_STORE } from "src/context/error-context";
import Console from "src/utils/console";
import { UUID } from "src/api/api_common";

const configuration = new Configuration({
    basePath: window.location.origin,
});

const defaultApi = new DefaultApi(configuration);

export const ApiInternal = {
    accounts: {
        getAll: () => handleError(defaultApi.getAllAccounts()),
        delete: (uuid: UUID) => handleError(defaultApi.deleteAccount({ uuid })),
    },
    settings: {
        getGeneral: () => handleError(defaultApi.getGeneralSettings()),
        setGeneral: (req: SetGeneralSettingsRequest) =>
            handleError(defaultApi.setGeneralSettings({ SetGeneralSettingsRequest: req })),
        getPortalAccessGroup: () => handleError(defaultApi.getPortalAccessGroup()),
        setPortalAccessGroup: (uuid: UUID) => handleError(defaultApi.setPortalAccessGroup({ SingleUuid: { uuid } })),
        getOidc: () => handleError(defaultApi.getOidcSettings()),
        updateOidc: (request: UpdateOidcSettingsRequest) =>
            handleError(defaultApi.updateOidcSettings({ UpdateOidcSettingsRequest: request })),
        getScim: () => handleError(defaultApi.getScimSettings()),
        setScim: (request: SetScimSettingsRequest) =>
            handleError(defaultApi.setScimSettings({ SetScimSettingsRequest: request })),
    },
    ldap: {
        testSettings: (request: SetLdapQuerySettingsRequest) =>
            handleError(defaultApi.testLdapQuerySettings({ SetLdapQuerySettingsRequest: request })),
        setQuerySettings: (request: SetLdapQuerySettingsRequest) =>
            handleError(defaultApi.setLdapQuerySettings({ SetLdapQuerySettingsRequest: request })),
        setConnectionSettings: (request: SetLdapConnectionSettingsRequest) =>
            handleError(defaultApi.setLdapConnectionSettings({ SetLdapConnectionSettingsRequest: request })),
        getSettings: () => handleError(defaultApi.getLdapSettings()),
    },
    users: {
        getAll: (request: GetAllUsersRequest) => handleError(defaultApi.getAllUsers({ GetAllUsersRequest: request })),
        expire: (uuid: UUID) => handleError(defaultApi.expireUser({ uuid })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleUser({ uuid: uuid })),
    },
    groups: {
        getAll: (request: GetAllGroupsRequest) =>
            handleError(defaultApi.getAllGroups({ GetAllGroupsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleGroup({ uuid })),
        getMembers: (uuid: UUID, request: GetSingleGroupMembersRequest) =>
            handleError(
                defaultApi.getSingleGroupMembers({
                    uuid,
                    GetSingleGroupMembersRequest: request,
                }),
            ),
        update: (uuid: UUID, request: UpdateGroupRequest) =>
            handleError(
                defaultApi.updateGroup({
                    uuid,
                    UpdateGroupRequest: request,
                }),
            ),
        local: {
            create: (request: CreateLocalGroupRequest) =>
                handleError(defaultApi.createLocalGroup({ CreateLocalGroupRequest: request })),
            delete: (uuid: UUID) => handleError(defaultApi.deleteLocalGroup({ uuid })),
            addMember: (group: UUID, user: UUID) =>
                handleError(
                    defaultApi.addLocalGroupMember({
                        uuid: group,
                        SingleUuid: { uuid: user },
                    }),
                ),
            removeMember: (group: UUID, user: UUID) =>
                handleError(
                    defaultApi.removeLocalGroupMember({
                        uuid: group,
                        SingleUuid: { uuid: user },
                    }),
                ),
        },
    },
    tags: {
        getAll: (request: GetAllTagsRequest) => handleError(defaultApi.getAllTags({ GetAllTagsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getTag({ uuid })),
        create: (request: CreateTagRequest) => handleError(defaultApi.createTag({ CreateTagRequest: request })),
        setName: (tag_uuid: UUID, name: string) =>
            handleError(
                defaultApi.setTagName({
                    tag_uuid,
                    SetTagNameRequest: { name },
                }),
            ),
        setDescription: (tag_uuid: UUID, description: string) =>
            handleError(
                defaultApi.setTagDescription({
                    tag_uuid,
                    SetTagDescriptionRequest: { description },
                }),
            ),
        delete: (uuid: UUID) => handleError(defaultApi.deleteTag({ uuid })),
    },
    subnets: {
        getAll: (request: GetAllSubnetsRequest) =>
            handleError(defaultApi.getAllSubnets({ GetAllSubnetsRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleSubnet({ uuid })),
        enable: (uuid: UUID, request: EnableSubnetRequest) =>
            handleError(
                defaultApi.enableSubnet({
                    uuid,
                    EnableSubnetRequest: request,
                }),
            ),
        disable: (uuid: UUID) => handleError(defaultApi.disableSubnet({ uuid })),
        delete: (uuid: UUID) => handleError(defaultApi.deleteSubnet({ uuid })),
        getPendingRoutes: (request: GetPendingRoutesRequest) =>
            handleError(defaultApi.getPendingSubnetRoutes({ GetPendingRoutesRequest: request })),
        approvePendingRoute: (uuid: UUID, request: SubnetsDescriptionRequest) =>
            handleError(defaultApi.approvePendingSubnetRoute({ uuid, SubnetsDescriptionRequest: request })),
        rejectPendingRoute: (uuid: UUID) => handleError(defaultApi.rejectPendingSubnetRoute({ uuid })),
        updateSubnet: (uuid: UUID, request: SubnetsDescriptionRequest) =>
            handleError(defaultApi.editSubnetDescription({ uuid, SubnetsDescriptionRequest: request })),
    },
    preAuthKeys: {
        getAll: (request: GetAllPreAuthKeysRequest) =>
            handleError(defaultApi.getAllPreAuthKeys({ GetAllPreAuthKeysRequest: request })),
        create: (request: CreatePreAuthKeyRequest) =>
            handleError(defaultApi.createPreAuthKey({ CreatePreAuthKeyRequest: request })),
        expirePreAuthKey: (uuid: UUID) => handleError(defaultApi.expireKey({ uuid })),
    },
    devices: {
        getAll: (request: GetAllDevicesRequest) =>
            handleError(defaultApi.getAllDevices({ GetAllDevicesRequest: request })),
        getSingle: (uuid: UUID) => handleError(defaultApi.getSingleDevice({ uuid })),
        getFromIp: (ip: string) => handleError(defaultApi.getDeviceFromIp({ ip })),
        setDnsname: (uuid: UUID, request: SetDeviceDnsnameRequest) =>
            handleError(
                defaultApi.setDeviceDnsname({
                    uuid,
                    SetDeviceDnsnameRequest: request,
                }),
            ),
        expireDevice: (uuid: UUID) => handleError(defaultApi.expireDevice({ uuid })),
        deleteDevice: (uuid: UUID) => handleError(defaultApi.deleteDevice({ uuid })),
        addDeviceTag: (device: UUID, tag: UUID) =>
            handleError(
                defaultApi.addDeviceTag({
                    uuid: device,
                    SingleUuid: { uuid: tag },
                }),
            ),
        removeDeviceTag: (device: UUID, tag: UUID) =>
            handleError(
                defaultApi.removeDeviceTag({
                    uuid: device,
                    SingleUuid: { uuid: tag },
                }),
            ),
        dumpDeviceState: (uuid: UUID) => handleError(defaultApi.dumpDeviceState({ uuid })),
    },
    changelog: {
        get: (request: GetChangelogRequest) => handleError(defaultApi.getChangelog({ GetChangelogRequest: request })),
    },
    accessRules: {
        getAll: () => handleError(defaultApi.getAllAccessRules()),
        getAllForDevice: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForDevice({ uuid })),
        getAllForGroup: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForGroup({ uuid })),
        getAllForUser: (uuid: UUID) => handleError(defaultApi.getAllAccessRulesForUser({ uuid })),
        getAllForOptions: () => handleError(defaultApi.getAllAccessRuleOptions()),
        create: (request: CreateAccessRuleRequest) =>
            handleError(defaultApi.createAccessRule({ CreateAccessRuleRequest: request })),
        update: (uuid: UUID, request: UpdateAccessRuleRequest) =>
            handleError(
                defaultApi.updateAccessRule({
                    uuid,
                    UpdateAccessRuleRequest: request,
                }),
            ),
        delete: (uuid: UUID) => handleError(defaultApi.deleteAccessRule({ uuid })),
    },
    dns: {
        get: () => handleError(defaultApi.getGlobalDns()),
        update: (request: FullGlobalDns) => handleError(defaultApi.updateGlobalDns({ FullGlobalDns: request })),
        getAllSplitDns: () => handleError(defaultApi.getSplitDns()),
        getAllExtraDns: (request: GetAllExtraDnsRequest) => handleError(defaultApi.getAllExtraDns(request)),
        getDeviceExtraDns: (uuid: UUID) => handleError(defaultApi.getDeviceExtraDns({ uuid })),
        deleteExtraDns: (uuid: UUID) => handleError(defaultApi.deleteExtraDnsDomain({ uuid })),
        deleteExtraDnsRecord: (uuid: UUID) => handleError(defaultApi.deleteExtraDnsRecord({ uuid })),
        addExtraDnsDomain: (request: AddExtraDnsRequest) =>
            handleError(defaultApi.addExtraDnsDomain({ AddExtraDnsRequest: request })),
        addSingleExtraDnsRecord: (uuid: UUID, request: AddExtraDnsRecordRequest) =>
            handleError(
                defaultApi.addRecordToExtraDns({
                    uuid,
                    AddExtraDnsRecordRequest: request,
                }),
            ),
        updateExtraDnsDomain: (uuid: UUID, request: UpdateExtraDnsDomainRequest) =>
            handleError(
                defaultApi.updateExtraDnsDomain({
                    uuid,
                    UpdateExtraDnsDomainRequest: request,
                }),
            ),
        updateExtraDnsRecord: (uuid: UUID, request: UpdateExtraDnsRecordRequest) =>
            handleError(
                defaultApi.updateExtraDnsRecord({
                    uuid,
                    UpdateExtraDnsRecordRequest: request,
                }),
            ),
        getPossibleExtraDnsByDevice: (device_uuid: UUID) =>
            handleError(defaultApi.getPossibleExtraDnsForDevice({ uuid: device_uuid })),
        getSearchDomains: () => handleError(defaultApi.getDnsSearchDomains()),
        insertSearchDomain: (request: AddDnsSearchDomainRequest) =>
            handleError(defaultApi.insertDnsSearchDomain({ AddDnsSearchDomainRequest: request })),
        moveSearchDomain: (request: MoveDnsSearchDomainRequest) =>
            handleError(defaultApi.moveDnsSearchDomain({ MoveDnsSearchDomainRequest: request })),
        removeSearchDomain: (domain_index: number) => handleError(defaultApi.removeDnsSearchDomain({ domain_index })),
        addDomainSplitDns: (request: CreateSplitDnsDomainRequest) =>
            handleError(defaultApi.addDomainToSplitDns({ CreateSplitDnsDomainRequest: request })),
        updateDomainSplitDns: (uuid: UUID, request: UpdateSplitDnsDomainRequest) =>
            handleError(defaultApi.updateDomainInSplitDns({ domain_uuid: uuid, UpdateSplitDnsDomainRequest: request })),
        deleteDomainSplitDns: (uuid: UUID) => handleError(defaultApi.deleteDomainFromSplitDns({ domain_uuid: uuid })),
        addResolverSplitDns: (uuid: UUID, request: CreateSplitDnsResolverRequest) =>
            handleError(
                defaultApi.addResolverToDomainInSplitDns({ domain_uuid: uuid, CreateSplitDnsResolverRequest: request }),
            ),
        updateResolverSplitDns: (uuid: UUID, request: UpdateSplitDnsResolverRequest) =>
            handleError(
                defaultApi.updateResolverInDomainInSplitDns({
                    resolver_uuid: uuid,
                    UpdateSplitDnsResolverRequest: request,
                }),
            ),
        deleteResolverSplitDns: (uuid: UUID) =>
            handleError(defaultApi.deleteResolverFromDomainInSplitDns({ resolver_uuid: uuid })),
    },
    exitNode: {
        getAll: (request: GetAllExitNodesRequest) =>
            handleError(defaultApi.getAllApprovedExitNodes({ GetAllExitNodesRequest: request })),
        getPending: (request: GetPendingExitNodesRequest) =>
            handleError(defaultApi.getPendingExitNodes({ GetPendingExitNodesRequest: request })),
        delete: (uuid: UUID) => handleError(defaultApi.deleteExitNode({ uuid })),
        reject: (uuid: UUID) => handleError(defaultApi.rejectPendingExitNode({ uuid })),
        approve: (uuid: UUID) => handleError(defaultApi.approvePendingExitNode({ uuid })),
        enable: (uuid: UUID) => handleError(defaultApi.enableExitNode({ uuid })),
        disable: (uuid: UUID) => handleError(defaultApi.disableExitNode({ uuid })),
    },
};

/**
 * Wraps a promise returned by the generated SDK which handles its errors and returns a {@link Result}
 *
 * @param promise The promise to wrap. This should be a promise defined in the generated part of the API
 *
 * @returns a new promise with a result that wraps errors from the API
 */
export async function handleError<T>(promise: Promise<T>): Promise<T> {
    try {
        return await promise;
    } catch (e) {
        let msg;
        if (e instanceof ResponseError) {
            if (e.response.status === 401) {
                msg = e.response.statusText;
            } else {
                try {
                    const err = await e.response.json();
                    msg = `${e.response.statusText}. TraceId: ${err.trace_id}`;
                } catch {
                    Console.error("Got invalid json", e.response.body);
                    msg = `${e.response.statusText}. The server's response was invalid json.`;
                }
            }
        } else if (e instanceof RequiredError) {
            Console.error(e);
            msg = "The server's response didn't match the spec";
        } else {
            Console.error("Unknown error occurred:", e);
            msg = "Unknown error occurred";
        }
        ERROR_STORE.report(msg);
        throw msg;
    }
}
